import type { OnboardingAnswers } from "../types";
import type { OnboardingTemplate } from "./types";

// Asset-class tag IDs (see src/widget-components/newsquawk-headlines/models/categories.ts)
const TAG = {
  crypto: "205463",
  energy: "195277",
  equities: "195276",
  fixedIncome: "195275",
  forex: "195274",
  metals: "195278",
} as const;

// Starter dashboard templates. Rules:
// - Only widgets a basic trialist can fully use (default-widgets / no
//   accessGroup) — never locked tiles. Tier upsell is relayed in copy, not
//   planted on the grid.
// - The PRIMARY NewsquawkHeadlines instance is never filtered: the full desk
//   wire keeps flowing (starter watchlists are merged in as highlights at
//   apply time). Narrower views live on SECONDARY instances via basic-filter.
// - Widget ids use pseudo-composite suffixes so a template can hold two
//   instances of the same widget; remapWidgetIds re-suffixes at apply time.
export const onboardingTemplates: OnboardingTemplate[] = [
  {
    id: "starter-equities",
    name: "Equities Trader",
    description:
      "The full Newsquawk wire alongside an equities-only feed, live charting, most-active movers and the economic calendar.",
    assetClasses: ["equities"],
    widgets: [
      { id: "NewsquawkHeadlines-primary", size: { w: 6, h: 9 } },
      { id: "TvAdvancedRealTimeChart", size: { w: 10, h: 5 } },
      {
        id: "NewsquawkHeadlines-equities",
        size: { w: 5, h: 4 },
        filter: { "basic-filter": [TAG.equities] },
      },
      { id: "TvActiveStocks", size: { w: 5, h: 4 } },
      { id: "EconomicCalendar", size: { w: 6, h: 4 } },
    ],
  },
  {
    id: "starter-macro-fx",
    name: "Macro & FX Trader",
    description:
      "Desk headlines with an FX & rates-focused feed, the economic calendar, US macro data and a cross-asset snapshot.",
    assetClasses: ["fx", "fixed_income", "macro"],
    widgets: [
      { id: "NewsquawkHeadlines-primary", size: { w: 6, h: 9 } },
      { id: "EconomicCalendar", size: { w: 5, h: 5 } },
      { id: "MarketSnapshot", size: { w: 5, h: 4 } },
      {
        id: "NewsquawkHeadlines-rates",
        size: { w: 6, h: 6 },
        filter: { "basic-filter": [TAG.forex, TAG.fixedIncome] },
      },
      { id: "UsMacroData", size: { w: 10, h: 6 } },
    ],
  },
  {
    id: "starter-energy",
    name: "Energy Trader",
    description:
      "The full wire plus an energy-only feed, energy social and web coverage, charting and the economic calendar.",
    assetClasses: ["energy"],
    widgets: [
      { id: "NewsquawkHeadlines-primary", size: { w: 6, h: 9 } },
      {
        id: "NewsquawkHeadlines-energy",
        size: { w: 5, h: 5 },
        filter: { "basic-filter": [TAG.energy] },
      },
      { id: "EnergyX", size: { w: 5, h: 4 } },
      { id: "EnergyWeb", size: { w: 5, h: 4 } },
      { id: "TvAdvancedRealTimeChart", size: { w: 10, h: 5 } },
      { id: "EconomicCalendar", size: { w: 5, h: 4 } },
    ],
  },
  {
    id: "starter-crypto",
    name: "Crypto Trader",
    description:
      "Desk headlines with a crypto-only feed, crypto web coverage, live charting and the economic calendar.",
    assetClasses: ["crypto"],
    widgets: [
      { id: "NewsquawkHeadlines-primary", size: { w: 6, h: 9 } },
      { id: "CryptoWeb", size: { w: 5, h: 5 } },
      {
        id: "NewsquawkHeadlines-crypto",
        size: { w: 5, h: 4 },
        filter: { "basic-filter": [TAG.crypto] },
      },
      { id: "TvAdvancedRealTimeChart", size: { w: 10, h: 5 } },
      { id: "EconomicCalendar", size: { w: 5, h: 4 } },
    ],
  },
  {
    id: "starter-multi-asset",
    name: "Multi-Asset Overview",
    description:
      "A broad view: the full wire, every external source combined, cross-asset snapshot, calendar and research.",
    assetClasses: ["metals", "macro"],
    widgets: [
      { id: "NewsquawkHeadlines-primary", size: { w: 6, h: 9 } },
      { id: "FirehoseAll", size: { w: 5, h: 5 } },
      { id: "EconomicCalendar", size: { w: 5, h: 4 } },
      { id: "MarketSnapshot", size: { w: 8, h: 5 } },
      { id: "TruthSocial", size: { w: 4, h: 4 } },
      { id: "ResearchHeadlines", size: { w: 4, h: 4 } },
    ],
  },
];

export const FALLBACK_TEMPLATE_ID = "starter-multi-asset";

/**
 * Picks the template with the greatest asset-class overlap with the user's
 * answers; ties resolve by catalogue order, no matches fall back to the
 * multi-asset overview.
 */
export function selectTemplate(
  answers: OnboardingAnswers | undefined
): OnboardingTemplate {
  const fallback = onboardingTemplates.find(
    (t) => t.id === FALLBACK_TEMPLATE_ID
  )!;
  const chosen = answers?.assetClasses?.length
    ? onboardingTemplates.reduce<{
        template: OnboardingTemplate | null;
        score: number;
      }>(
        (best, template) => {
          const score = template.assetClasses.filter((a) =>
            answers.assetClasses.includes(a)
          ).length;
          return score > best.score ? { template, score } : best;
        },
        { template: null, score: 0 }
      ).template
    : null;
  return chosen ?? fallback;
}

export function getTemplateById(id: string): OnboardingTemplate | undefined {
  return onboardingTemplates.find((t) => t.id === id);
}
